import React from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent } from './Shared';
import { ProductVideo } from './ProductVideo';
import { featuredTestimonials } from '../data/testimonials';
import { ShieldCheck, Lock, EuroIcon, Star, X, ArrowRightIcon, FileTextIcon, ZapIcon, LayersIcon, TrophyIcon } from 'lucide-react';
export const HERO_MOCKUP = 'https://cdn.magicpatterns.com/uploads/4Fd8jSTsTYAyNvxhPyhL3m/mockup_png.png';
const PRODUCT_VIDEO = 'https://cdn.magicpatterns.com/uploads/3M77p7oEThDni9tvmS68yn/video.mp4';
export const Hero = () => {
  const chips = [{
    icon: FileTextIcon,
    label: '100% PDF'
  }, {
    icon: ZapIcon,
    label: 'Accesso in 5 min'
  }, {
    icon: LayersIcon,
    label: '365 sessioni'
  }];
  return <section className="relative overflow-hidden border-b border-white/5 bg-slate-950" data-id="element-291" data-mp="e25bzndwzmk7">
      {/* Subtle top light — solid, not a gradient */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-sky-400/40" aria-hidden="true" data-id="element-292" data-mp="1pfhi64x5ch9j" />
      {/* minmax(0,1fr) keeps the mobile column exactly the screen width, even
          when content (or a translated label) is wider — nothing gets pushed right. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] items-center justify-items-center gap-3 px-5 pb-10 pt-5 sm:gap-6 sm:pt-8 md:px-8 md:pb-20 md:pt-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:justify-items-stretch lg:gap-x-12 lg:gap-y-8" data-id="element-293" data-mp="1c9eex4ltt15s">
        {/* Headline — fixed line breaks on mobile, fluid size so every line fits */}
        <FadeIn className="flex w-full min-w-0 flex-col items-center text-center lg:col-start-1 lg:row-start-1 lg:items-start lg:self-end lg:text-left" data-id="element-294" data-mp="tj6jz9sa5h0o">
          <p className="mb-2.5 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-sky-400 sm:mb-4 sm:text-xs" data-id="element-295" data-mp="kkbey10z2cyy">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" aria-hidden="true" data-id="element-296" data-mp="fk6uy10uv03z" />
            Megapack Funzionale 365™
          </p>
          {/* Italic glyphs lean right; a tiny right padding re-centers them optically */}
          <h1 className="w-full break-words pr-[0.06em] font-display text-[clamp(30px,9.6vw,42px)] font-black uppercase italic leading-[0.88] tracking-tight text-white sm:text-5xl sm:leading-[0.96] md:text-6xl lg:pr-0 lg:text-[54px] lg:leading-[1] xl:text-[60px]" data-id="element-297" data-mp="5x2xiih2lbej">
            <span className="block sm:inline" data-id="element-298" data-mp="3s4sziiikdyj0">Pianifica</span>{' '}
            <span className="block sm:inline" data-id="element-299" data-mp="jpskoq8c4md8">un anno intero</span>{' '}
            <span className="block sm:inline" data-id="element-300" data-mp="onkke6f81crz">di lezioni</span>{' '}
            <span className="block sm:inline" data-id="element-301" data-mp="opx5gsga0399"><Accent data-id="element-302" data-mp="9zhf6z906548">funzionali.</Accent></span>
            <span className="mt-1 block text-slate-400" data-id="element-303" data-mp="9yo5bpeqyzhv">In 5 minuti.</span>
          </h1>
          <p className="mx-auto mt-3 max-w-[320px] text-[15px] leading-snug text-slate-300 sm:mt-5 sm:max-w-md sm:text-base sm:leading-relaxed md:text-lg lg:mx-0" data-id="element-304" data-mp="ewg8jfkue81d">
            365 sessioni pronte da insegnare, che tu sia un coach esperto o
            abbia appena iniziato.
          </p>
        </FadeIn>

        {/* Mockup */}
        <FadeIn delay={0.1} className="flex w-full min-w-0 justify-center lg:col-start-2 lg:row-span-2 lg:row-start-1" data-id="element-305" data-mp="2s2e788sr3yiu">
          <a href="#pricing" className="group block w-full" aria-label="Vedi l'offerta" data-id="element-306" data-mp="31uajcnro9p62">
            <img src={HERO_MOCKUP} alt="Megapack Funzionale 365 — i 6 volumi, il pacchetto bonus e l'accesso digitale su tablet, computer e smartphone" width={1536} height={1024} loading="eager" decoding="async"
          // @ts-ignore fetchpriority is valid HTML
          fetchpriority="high" className="mx-auto block h-auto w-full max-w-[400px] object-contain sm:max-w-[560px] transition-transform duration-200 ease-out group-hover:-translate-y-1 lg:max-w-none" data-id="element-307" data-mp="2wvh34nl03k27" />
          </a>
        </FadeIn>

        {/* CTA */}
        <FadeIn delay={0.15} className="flex w-full min-w-0 max-w-[420px] flex-col items-center sm:max-w-none lg:col-start-1 lg:row-start-2 lg:items-start lg:self-start" data-id="element-308" data-mp="3pkwh9m7ypt2c">
          <ul className="mb-4 flex w-full flex-wrap items-center justify-center gap-1.5 sm:mb-5 sm:gap-2 lg:justify-start" data-id="element-309" data-mp="sq8txo8h43wc">
            {chips.map(({
            icon: Icon,
            label
          }) => <li key={label} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs md:text-sm" data-id="element-310" data-mp="xry8veyth1pp">
                <Icon size={14} className="text-sky-400" aria-hidden="true" data-id="element-311" data-mp="1bna2fj18km51" />
                {label}
              </li>)}
          </ul>
          <CTAButton href="#pricing" className="w-full sm:w-auto" data-id="element-312" data-mp="1lffsgn7xgthl">
            Voglio il Megapack
            <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-313" data-mp="1qf24wufxm5pc" />
          </CTAButton>
          <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[11px] text-slate-400 sm:mt-4 sm:gap-x-4 sm:gap-y-1.5 sm:text-xs lg:justify-start" data-id="element-314" data-mp="1vazm12z0rg36">
            <span className="flex items-center gap-1.5" data-id="element-315" data-mp="3ayo3jk48irxz">
              <ShieldCheck size={14} className="text-emerald-400" aria-hidden="true" data-id="element-316" data-mp="35y9wa4agjm10" /> Garanzia 7 giorni
            </span>
            <span className="flex items-center gap-1.5" data-id="element-317" data-mp="30zyk3mwowzfm">
              <Lock size={14} aria-hidden="true" data-id="element-318" data-mp="2grlpp3aw0ww" /> Pagamento sicuro
            </span>
            <span className="flex items-center gap-1.5" data-id="element-319" data-mp="3tn2pvu3lxhsg">
              <EuroIcon size={14} className="text-sky-400" aria-hidden="true" data-id="element-320" data-mp="3tnkvhhc5t8d1" /> Prezzo in euro
            </span>
          </div>
        </FadeIn>
      </div>
    </section>;
};
export const SocialProof = () => {
  const stats = [{
    value: '+500',
    label: 'coach si fidano'
  }, {
    value: '6',
    label: 'volumi pronti'
  }, {
    value: '5',
    label: 'bonus inclusi'
  }, {
    value: '4.9/5',
    label: 'valutazione media',
    star: true
  }];
  return <div className="border-b border-white/5 bg-slate-900/40 px-5 py-8 md:px-8" data-id="element-321" data-mp="2anlec0jl09s9">
      <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-y-6 md:grid-cols-4" data-id="element-322" data-mp="2fn80x0lav2dq">
        {stats.map((s, i) => <FadeIn key={s.label} delay={Math.min(i * 0.05, 0.2)} className={`text-center ${i % 2 === 1 ? 'border-l border-white/10' : ''} ${i > 0 ? 'md:border-l md:border-white/10' : ''}`} data-id="element-323" data-mp="20ytqo4lk0bn5" data-mpi={i}>
            <dt className="sr-only" data-id="element-324" data-mp="1rxvkpgrk1u1x">{s.label}</dt>
            <dd className="font-display text-4xl font-black italic leading-none tracking-tight text-white md:text-5xl" data-id="element-325" data-mp="1rw2chx1an1rt">
              {s.value}
            </dd>
            <dd className="mt-2 flex items-center justify-center gap-1 text-sm text-slate-400" data-id="element-326" data-mp="1hzz9koymaz3t">
              {s.label}
              {s.star && <Star size={12} className="fill-amber-400 text-amber-400" aria-hidden="true" data-id="element-327" data-mp="1mzbzjikscmam" />}
            </dd>
          </FadeIn>)}
      </dl>
    </div>;
};
const Stars = ({
  size = 12
}: {
  size?: number;
}) => <div className="flex text-amber-400" role="img" aria-label="5 stelle su 5" data-id="element-328" data-mp="3hbsagwoj0olt">
    {[...Array(5)].map((_, j) => <Star key={j} size={size} className="fill-current" aria-hidden="true" data-id="element-329" data-mp="15mvjm15sz1jw" data-mpi={j} />)}
  </div>;
export const Testimonials1 = () => <Section className="bg-slate-950 !py-12 md:!py-16" data-id="element-330" data-mp="1ffjhkz8irbk7">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-331" data-mp="1fh4aylj3kogh">
      {featuredTestimonials.map((t, i) => <FadeIn key={t.author} delay={Math.min(i * 0.05, 0.2)} data-id="element-332" data-mp="1am896to26mq6" data-mpi={i}>
          <figure className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6" data-id="element-333" data-mp="1oeeko2g9o5ja">
            <Stars data-id="element-334" data-mp="1jgd3xo6bciaz" />
            <blockquote className="mt-3 text-[15px] leading-relaxed text-slate-200" data-id="element-335" data-mp="1jiz9xjdk2pr3">
              “{t.text}”
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3 pt-5" data-id="element-336" data-mp="1te9a01ke8n28">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.bg} text-xs font-bold text-white`} data-id="element-337" data-mp="3zuu86rcsaiv">
                {t.initials}
              </div>
              <div className="min-w-0" data-id="element-338" data-mp="3v88k4jls4ydc">
                <p className="text-sm font-semibold leading-tight text-white" data-id="element-339" data-mp="39drwcadp60nb">{t.author}</p>
                <p className="text-xs text-slate-500" data-id="element-340" data-mp="3ebjlwxqvdaoc">{t.role}</p>
              </div>
            </figcaption>
          </figure>
        </FadeIn>)}
    </div>
  </Section>;
export { Stars };
export const Problem = () => {
  const painPoints = ['Entri in palestra senza sapere esattamente quale lezione farai oggi', 'Passi ore a cercare esercizi adatti al tuo gruppo', 'Ripeti sempre gli stessi circuiti perché non hai una libreria di sessioni', 'Arriva un nuovo iscritto e non sai come adattare la lezione al volo', 'Vorresti un programma annuale ma non hai il tempo di costruirlo', 'Il tuo gruppo si blocca ogni volta che provi a dargli una vera progressione', 'Weekend a cercare idee. Lunedì a improvvisare. Sempre lo stesso ciclo.'];
  return <Section className="bg-slate-900/40" data-id="element-341" data-mp="jno7i9ru0uty">
      <FadeIn data-id="element-342" data-mp="jmd5ofhy5x5h">
        <SectionTitle data-id="element-343" data-mp="jm3guv8n1w1d">
          <span className="mx-auto block max-w-4xl" data-id="element-344" data-mp="jpiuruqksz17">
            Se insegni <Accent data-id="element-345" data-mp="244fdikmywpy3">allenamento funzionale</Accent>, questo ti
            suonerà familiare…
          </span>
        </SectionTitle>
      </FadeIn>

      <ul className="mx-auto mb-16 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-1 md:mb-24 md:grid-cols-2" data-id="element-346" data-mp="1vdezto3a1kld">
        {painPoints.map((point, i) => <li key={point} className={`flex items-start gap-3 border-b border-white/5 py-3.5 ${i === painPoints.length - 1 ? 'md:col-span-2 md:mx-auto md:max-w-md md:border-b-0' : ''}`} data-id="element-347" data-mp="1qdixdta8a17x" data-mpi={i}>
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500/15" data-id="element-348" data-mp="1gf32rgbzs9to">
              <X size={12} className="text-red-400" strokeWidth={3} aria-hidden="true" data-id="element-349" data-mp="1cfc8v3r65dkx" />
            </span>
            <p className="text-[15px] leading-normal text-slate-300 md:text-base" data-id="element-350" data-mp="17h0womdeiqzj">{point}</p>
          </li>)}
      </ul>

      <FadeIn className="mx-auto mb-12 grid max-w-5xl items-center gap-6 md:mb-16 md:grid-cols-2 md:gap-12" data-id="element-351" data-mp="36q7our4onuxq">
        <h3 className="text-center font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-left md:text-4xl md:leading-[1] lg:text-5xl lg:leading-[1.02]" data-id="element-352" data-mp="ibmvyoslzs2u">
          Stai ancora <Accent data-id="element-353" data-mp="rc31s4wtrbav">improvvisando</Accent> i tuoi allenamenti?
        </h3>
        <div className="space-y-4 text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-354" data-mp="r9yxhlv0kaa2">
          <p data-id="element-355" data-mp="mb5gvkkfk35o">
            Hai scelto questa professione perché ami l’allenamento funzionale e
            vedere crescere il tuo gruppo.
          </p>
          <p data-id="element-356" data-mp="3qs9drp34r0un">
            Che tu sia un coach esperto o alle prime armi, il problema è lo
            stesso: passi più tempo a <strong className="font-semibold text-white" data-id="element-357" data-mp="8e8txu7fn5u2">CERCARE</strong> cosa insegnare che a{' '}
            <strong className="font-semibold text-white" data-id="element-358" data-mp="3lyvtqw5yzgp">INSEGNARE</strong> davvero. E mentre perdi le tue
            domeniche a costruire sessioni, la concorrenza continua ad andare avanti.
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mx-auto max-w-3xl" data-id="element-359" data-mp="371edneasadce">
        <div className="rounded-3xl border border-sky-400/40 bg-blue-600/10 px-6 py-8 text-center md:px-10 md:py-10" data-id="element-360" data-mp="1ml6ik06vs6cs">
          <p className="font-display text-2xl font-black uppercase italic leading-[1.02] tracking-tight text-white md:text-4xl md:leading-[1.08]" data-id="element-361" data-mp="1rcydso0zf099">
            E se questa fosse l’<Accent data-id="element-362" data-mp="1ijbj718ig31n">ultima lezione</Accent>
            <br data-id="element-363" data-mp="1ikcth3hpclib" /> che prepari in questo modo?
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mt-20 text-center md:mt-28" data-id="element-364" data-mp="26aqp13zhx2hn">
        <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-950" data-id="element-365" data-mp="2aar3upqibfp8">
          <TrophyIcon size={14} aria-hidden="true" data-id="element-366" data-mp="1waq6vldd4sf1" />
          Il sistema n.1 per i coach di allenamento funzionale
        </p>
        <h2 className="font-display text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-6xl" data-id="element-367" data-mp="21c5qd8zw6qvb">
          Megapack Funzionale <Accent data-id="element-368" data-mp="pw77kdewwqgw">365™</Accent>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-369" data-mp="pst75apt6rc3">
          6 Volumi + 5 Bonus — 365 sessioni pronte · Un anno intero · Tutto
          risolto.
        </p>

        <div className="relative mx-auto mt-10 max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/50" data-id="element-370" data-mp="1ttzt0xxbr4r">
          <ProductVideo src={PRODUCT_VIDEO} label="Anteprima dei volumi di Megapack Funzionale 365" className="block w-full object-cover" data-id="element-371" data-mp="3o6994cd9k19d" />
        </div>
      </FadeIn>
    </Section>;
};